import { useMemo } from "react";
import { Table, Button, Card, ConfigProvider, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { ArrowRightOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useGetFamily } from "network/queries/family.query";
import {
  IFamily,
  IChildInfo,
  ICarInfo,
  IFarmlandInfo,
} from "network/models/family.model";

const getFullName = (p?: {
  firstName?: string;
  middleName?: string;
  lastName?: string;
}) =>
  [p?.firstName, p?.middleName, p?.lastName].filter(Boolean).join(" ") || "-";

const fmtDate = (d?: string | Date) => {
  if (!d) return "-";
  const dt = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toISOString().slice(0, 10);
};

const InfoItem = ({
  label,
  value,
  tagColor,
}: {
  label: string;
  value?: string | number | null;
  tagColor?: string;
}) => {
  if (tagColor) {
    return (
      <div className="flex flex-col border-b border-gray-100 pb-2">
        <span className="text-gray-500 text-sm sm:text-base">{label}</span>
        <Tag color={tagColor} className="mt-1 text-sm sm:text-base">
          {value || "-"}
        </Tag>
      </div>
    );
  }

  return (
    <div className="flex flex-col border-b border-gray-100 pb-2">
      <span className="text-gray-500 text-sm sm:text-base">{label}</span>
      <span className="font-medium text-gray-800 text-base sm:text-lg">
        {value || "-"}
      </span>
    </div>
  );
};

export default function FamilyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: resp, isLoading } = useGetFamily(id || "");
  const family = (resp?.data ?? null) as IFamily | null;

  const childColumns: ColumnsType<IChildInfo> = useMemo(
    () => [
      {
        title: "الاسم الكامل",
        key: "fullName",
        render: (_, r) => (
          <Tag color="blue" className="px-3 py-1 text-base font-semibold">
            {getFullName(r)}
          </Tag>
        ),
      },
      {
        title: "تاريخ الميلاد",
        dataIndex: "birthdate",
        key: "birthdate",
        render: (d) => fmtDate(d),
      },
      {
        title: "المستوى التعليمي",
        dataIndex: "education",
        key: "education",
        render: (edu) => edu || "-",
      },
      {
        title: "المهنة",
        dataIndex: "professional",
        key: "professional",
        render: (v) => v || "-",
      },
      {
        title: "المسمى الوظيفي",
        dataIndex: "jobTitle",
        key: "jobTitle",
        render: (v) => v || "-",
      },
      {
        title: "مكان العمل",
        dataIndex: "workplace",
        key: "workplace",
        render: (v) => v || "-",
      },
      {
        title: "الراتب الشهري",
        dataIndex: "salary",
        key: "salary",
        render: (v) => (v != null ? v : "-"),
      },
      {
        title: "الدخل السنوي",
        dataIndex: "income",
        key: "income",
        render: (v) => (v != null ? v : "-"),
      },
      {
        title: "النفقات المتوقعة",
        dataIndex: "expectedExpenses",
        key: "expectedExpenses",
        render: (v) => (v != null ? v : "-"),
      },
      {
        title: "الحالة الصحية",
        dataIndex: "healthStatus",
        key: "healthStatus",
        render: (v) => (
          <Tag color={v === "جيدة" || v === "سليم" ? "green" : "red"}>
            {v || "-"}
          </Tag>
        ),
      },
    ],
    []
  );

  const carColumns: ColumnsType<ICarInfo> = useMemo(
    () => [
      { title: "النوع", dataIndex: "type", key: "type" },
      {
        title: "الشركة / الطراز",
        key: "makeModel",
        render: (_, r) => `${r.make || "-"} / ${r.model || "-"}`,
      },
      { title: "سنة الصنع", dataIndex: "year", key: "year" },
      { title: "اللون", dataIndex: "color", key: "color" },
      {
        title: "نوع الوقود",
        dataIndex: "fuelType",
        key: "fuelType",
        render: (v) => v || "-",
      },
      { title: "رقم اللوحة", dataIndex: "plateNumber", key: "plateNumber" },
      { title: "VIN", dataIndex: "vin", key: "vin" },
      {
        title: "تاريخ التسجيل",
        dataIndex: "registrationDate",
        key: "registrationDate",
        render: (d) => fmtDate(d),
      },
      {
        title: "القيمة التقديرية",
        dataIndex: "estimatedValue",
        key: "estimatedValue",
      },
      { title: "ملاحظات", dataIndex: "notes", key: "notes", ellipsis: true },
    ],
    []
  );

  const landColumns: ColumnsType<IFarmlandInfo> = useMemo(
    () => [
      { title: "اسم الأرض", dataIndex: "name", key: "name" },
      { title: "الموقع", dataIndex: "location", key: "location" },
      { title: "رقم السجل/الطابو", dataIndex: "deedNumber", key: "deedNumber" },
      {
        title: "المساحة (هكتار)",
        dataIndex: "areaHectares",
        key: "areaHectares",
      },
      { title: "نوع المحصول", dataIndex: "cropType", key: "cropType" },
      {
        title: "القيمة التقديرية",
        dataIndex: "estimatedValue",
        key: "estimatedValue",
      },
      {
        title: "الدخل السنوي المتوقع",
        dataIndex: "expectedAnnualIncome",
        key: "expectedAnnualIncome",
      },
      { title: "ملاحظات", dataIndex: "notes", key: "notes", ellipsis: true },
    ],
    []
  );

  if (isLoading || !family) {
    return (
      <p className="p-6 font-semibold text-lg sm:text-xl">⏳ جاري التحميل...</p>
    );
  }

  return (
    <ConfigProvider direction="rtl">
      <div className="p-4 sm:p-6 flex justify-center bg-gray-50 min-h-screen">
        <div className="w-full max-w-6xl space-y-6">
          {/* ===== Father Info ===== */}
          <Card
            className="shadow-sm rounded-2xl border border-gray-200 bg-white hover:shadow-md transition-all duration-300"
            title={
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                  👨 بيانات الأب
                </h2>
                <Tag color="blue-inverse" className="text-sm sm:text-base">
                  رب الأسرة
                </Tag>
              </div>
            }
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4 text-gray-700 text-base sm:text-lg leading-relaxed py-2">
              <InfoItem
                label="الاسم الكامل"
                value={getFullName(family.father)}
              />
              <InfoItem
                label="تاريخ الميلاد"
                value={fmtDate(family.father.birthdate)}
              />
              <InfoItem label="الرقم الوطني" value={family.father.nationalId} />
              <InfoItem label="المهنة" value={family.father.professional} />
              <InfoItem label="المسمى الوظيفي" value={family.father.jobTitle} />
              <InfoItem label="مكان العمل" value={family.father.workplace} />
              <InfoItem label="الراتب الشهري" value={family.father.salary} />
              <InfoItem label="الدخل السنوي" value={family.father.income} />

              <InfoItem
                label="نفقات الأسرة المتوقعة"
                value={family.father.expectedFamilyExpenses}
              />
              <InfoItem label="مكان الإقامة" value={family.father.village} />
              <InfoItem label="الهاتف" value={family.father.phone} />
              <InfoItem
                label="الحالة الصحية"
                value={family.father.healthStatus}
                tagColor={
                  family.father.healthStatus === "جيدة" ? "green" : "red"
                }
              />
            </div>
          </Card>

          {/* ===== Mother Info ===== */}
          <Card
            className="shadow-sm rounded-2xl border border-gray-200 bg-white hover:shadow-md transition-all duration-300"
            title={
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                  👩 بيانات الأم
                </h2>
                <Tag color="pink-inverse" className="text-sm sm:text-base">
                  زوجة رب الأسرة
                </Tag>
              </div>
            }
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4 text-gray-700 text-base sm:text-lg leading-relaxed py-2">
              <InfoItem
                label="الاسم الكامل"
                value={getFullName(family.mother)}
              />
              <InfoItem
                label="تاريخ الميلاد"
                value={fmtDate(family.mother.birthdate)}
              />
              <InfoItem label="الرقم الوطني" value={family.mother.nationalId} />
              <InfoItem label="المهنة" value={family.mother.professional} />
              <InfoItem label="المسمى الوظيفي" value={family.mother.jobTitle} />
              <InfoItem label="مكان العمل" value={family.mother.workplace} />
              <InfoItem label="الراتب الشهري" value={family.mother.salary} />
              <InfoItem label="الدخل السنوي" value={family.mother.income} />

              <InfoItem
                label="نفقات الأسرة المتوقعة"
                value={family.mother.expectedFamilyExpenses}
              />
              <InfoItem label="الهاتف" value={family.mother.phone} />
              <InfoItem
                label="الحالة الصحية"
                value={family.mother.healthStatus}
                tagColor={
                  family.mother.healthStatus === "جيدة" ? "green" : "red"
                }
              />
            </div>
          </Card>

          {/* Children */}
          <Card title="👨‍👩‍👧‍👦 الأبناء" className="shadow-lg">
            <div className="overflow-x-auto">
              <Table
                rowKey={(_, i) => String(i)}
                dataSource={family.children || []}
                columns={childColumns}
                bordered
                pagination={{ pageSize: 5 }}
                locale={{ emptyText: "لا يوجد أبناء" }}
                className="min-w-[800px]"
              />
            </div>
          </Card>

          {/* Cars */}
          <Card title="🚗 المركبات" className="shadow-lg">
            <div className="overflow-x-auto">
              <Table
                rowKey={(_, i) => String(i)}
                dataSource={family.cars || []}
                columns={carColumns}
                bordered
                pagination={{ pageSize: 5 }}
                locale={{ emptyText: "لا توجد مركبات" }}
                className="min-w-[800px]"
              />
            </div>
          </Card>

          {/* Farmlands */}
          <Card title="🌾 الأراضي الزراعية" className="shadow-lg">
            <div className="overflow-x-auto">
              <Table
                rowKey={(_, i) => String(i)}
                dataSource={family.farmlands || []}
                columns={landColumns}
                bordered
                pagination={{ pageSize: 5 }}
                locale={{ emptyText: "لا توجد أراضٍ زراعية" }}
                className="min-w-[800px]"
              />
            </div>
          </Card>

          {/* Footer Button */}
          <div className="flex justify-end sticky bottom-0 bg-white py-4 border-t border-gray-200">
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              size="large"
              className="text-base sm:text-lg font-semibold px-6"
              onClick={() => navigate("/")}
            >
              رجوع إلى القائمة
            </Button>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
}
